import { useNavigate } from "react-router";

import { t } from "i18next";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { articleFilenameToSlugAtom } from "@/src/state/articleFilenameToSlugAtom";
import { articleListAtom } from "@/src/state/articleListAtom";
import { articlesAtom } from "@/src/state/articlesAtom";

import { PageContainer } from "@/src/components/atoms";

import { dashesToUnderscores } from "@/src/pages/consumer/BlogPage/BlogPage";
import { ArticleCard } from "@/src/pages/consumer/BlogPage/components/ArticleCard";
import { ArticleCardSkeleton } from "@/src/pages/consumer/BlogPage/components/ArticleCardSkeleton";
import { HOMEPAGE_CONTAINER_STYLES } from "@/src/pages/consumer/HomePage/homePage.constants";

import { articlesFromFilenameList } from "@/src/utils/articleSyncHelpers";
import { getArticleUrl, useGetArticles } from "@/src/utils/blogUtils";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtomValue } from "jotai";

const HomePageArticleBlock = () => {
  const sendDataToGTM = useGTMDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const filenames = useAtomValue(articleListAtom);
  const filenameToSlug = useAtomValue(articleFilenameToSlugAtom);
  const articles = useAtomValue(articlesAtom);

  const { isLoading } = useGetArticles();
  const articleList = articlesFromFilenameList(
    filenames,
    filenameToSlug,
    articles
  ).slice(0, 4);

  const handleSendGtmEvent = (gtmEvent: string) => {
    sendDataToGTM({
      event: { gtmEvent },
      user_type: "consumer"
    });
  };

  const showSkeletons = isLoading;

  if (!isLoading && articleList.length === 0) {
    return <PageContainer maxWidth="1200px" sx={{ margin: "auto" }} />;
  }

  const articleCards = (
    <Grid container sx={{ maxWidth: isDesktop ? 1000 : "100%", mx: "auto" }}>
      {showSkeletons
        ? [1, 2, 3, 4].map((i) => (
            <Grid
              key={`skeleton-${i}`}
              size={{
                xs: 12,
                sm: 6,
                lg: 3
              }}
            >
              <ArticleCardSkeleton />
            </Grid>
          ))
        : articleList.map((article) => (
            <Grid
              key={`blog-article-card-grid-${article.date}`}
              size={{
                xs: 12,
                sm: 6,
                lg: 3
              }}
            >
              <ArticleCard
                to={`/blog/${getArticleUrl(article.title)}`}
                articleData={article}
                titleSize="medium"
                onClick={() => {
                  handleSendGtmEvent(
                    `article_card_${dashesToUnderscores(article.date)}_clicked`
                  );
                }}
              />
            </Grid>
          ))}
    </Grid>
  );

  return (
    <Box
      sx={{
        ...HOMEPAGE_CONTAINER_STYLES,
        background: theme.palette.misc.timelineBackground
      }}
    >
      <Typography variant="h2">
        {t("consumer.homePage.articleBlockTitle")}
      </Typography>
      {articleCards}
      {!showSkeletons && (
        <Box
          sx={{
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-end"
          }}
        >
          <Button
            variant="text"
            onClick={() => {
              handleSendGtmEvent("show_more_articles_clicked");
              navigate("/blog");
            }}
            endIcon={<ArrowForwardIcon />}
          >
            {t("general.showMore")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HomePageArticleBlock;
